/**
 * A single artist in an event/course lineup, as serialized by the Eventbrite
 * catalog job into the product's `c_eventLineUp` custom attribute.
 */
export interface EventLineUpArtist {
    id: string | null;
    name: string | null;
    biography: string | null;
    imageUrl: string | null;
}

function toStringOrNull(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Parses the product's `c_eventLineUp` JSON string into a validated array of
 * lineup artists. Returns an empty array for missing, malformed, or empty data,
 * so callers can treat "no lineup" and "unparseable lineup" the same way.
 *
 * @param raw - The raw `c_eventLineUp` attribute value (a JSON array string)
 * @returns The parsed artists, or an empty array when there is nothing to show
 */
export function parseEventLineUp(raw: string | undefined | null): EventLineUpArtist[] {
    if (!raw) {
        return [];
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return [];
    }

    if (!Array.isArray(parsed)) {
        return [];
    }

    const artists: EventLineUpArtist[] = [];
    for (const entry of parsed) {
        if (!entry || typeof entry !== 'object') {
            continue;
        }
        const artist = entry as Record<string, unknown>;
        const name = toStringOrNull(artist.name);
        const biography = toStringOrNull(artist.biography);
        const imageUrl = toStringOrNull(artist.imageUrl);
        // Skip entries with no displayable content at all.
        if (!name && !biography && !imageUrl) {
            continue;
        }
        artists.push({
            id: toStringOrNull(artist.id),
            name,
            biography,
            imageUrl,
        });
    }

    return artists;
}
